<template>
  <div>
    <content-loading v-if="isLoading" />
    <div v-else>
      <div v-if="pagination && pagination.docs && pagination.docs.length > 0">
        <div class="table-responsive">
          <table class="table table-centered table-bordered table-nowrap mb-0 table-hover">
            <thead class="thead-light">
              <tr>
                <th style="width: 150px">{{ $t("audit-control.history.date_user") }}</th>
                <th>{{ $t("audit-control.history.action.text") }}</th>
                <th>{{ $t("audit-control.control.field.status") }}</th>
                <th>{{ $t("audit-control.control.field.manager") }}</th>
                <th>{{ $t("audit-control.control.field.criticality") }}</th>
                <th>{{ $t("audit-control.control.field.riskNote") }}</th>
                <th>{{ $t("audit-control.control.category") }}</th>
                <th>{{ $t("audit-control.control.type") }}</th>
                <th>{{ $t("audit-control.related-standards") }}</th>
                <th>{{ $t("audit-control.control.field.risks") }}</th>
                <th>{{ $t("audit-control.control.field.complianceItems") }}</th>
                <th>{{ $t("audit-control.approval.status.text") }}</th>
                <th>{{ $t("audit-control.approval.assignee") }}</th>
                <th>{{ $t("audit-control.approval.note") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pagination.docs" :key="item._id">
                <td>
                  <small class="d-block text-muted">{{ item.createdAt | date }}</small>
                  <span class="font-weight-bold">{{ item.accountDisplayName }}</span>
                </td>
                <td>
                  <b-badge :variant="getActionVariant(item.action)">{{ $t(`audit-control.history.action.${item.action}`) }}</b-badge>
                  <div v-if="item.description" class="small text-muted mt-1">{{ item.description }}</div>
                  
                  <!-- Detailed changes loop -->
                  <div v-if="item.changes && Object.keys(item.changes).length > 0" class="mt-2 text-wrap" style="max-width: 300px;">
                    <div v-for="(change, key) in item.changes" :key="key" class="font-size-12 mb-1 border-left pl-2">
                      <span class="text-muted font-weight-bold mr-1">{{ getFieldLabel(key) }}:</span>
                      <span class="text-danger strike-through">{{ formatValue(change.old, key) }}</span>
                      <span class="mx-1 text-muted"><i class="bx bx-right-arrow-alt"></i></span>
                      <span class="text-success">{{ formatValue(change.new, key) }}</span>
                    </div>
                  </div>
                </td>

                <!-- Snapshot Fields with Highlighting -->
                <td :class="{ 'bg-soft-warning': isChanged(item, 'status') }">
                  <b-badge v-if="getSnapshotValue(item, 'status')" :variant="getStatusVariant(getSnapshotValue(item, 'status'))">
                    {{ $t(`audit-control.status.${getSnapshotValue(item, "status").toLowerCase()}`) }}
                  </b-badge>
                  <span v-else>-</span>
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'manager') }">
                  {{ getLocalizedValue(getSnapshotValue(item, "manager")) || "-" }}
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'criticality') }">
                  {{ getSnapshotValue(item, "criticality") ? $t(`audit-control.criticality.${getSnapshotValue(item, "criticality")}`) : "-" }}
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'riskNote') }">
                  <span v-b-tooltip.hover :title="getLocalizedValue(getSnapshotValue(item, 'riskNote'))" class="d-inline-block text-truncate" style="max-width: 150px;">
                    {{ getLocalizedValue(getSnapshotValue(item, "riskNote")) || "-" }}
                  </span>
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'category') }">
                  <span
                    v-if="getSnapshotValue(item, 'category')"
                    v-b-tooltip.hover
                    :title="getLocalizedValue(getSnapshotValue(item, 'category').name)"
                    class="d-inline-block text-truncate"
                    style="max-width: 150px;"
                  >
                    {{ getLocalizedValue(getSnapshotValue(item, "category").name) || "-" }}
                  </span>
                  <span v-else>-</span>
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'type') }">
                  <span
                    v-if="getSnapshotValue(item, 'type')"
                    v-b-tooltip.hover
                    :title="getLocalizedValue(getSnapshotValue(item, 'type').name)"
                    class="d-inline-block text-truncate"
                    style="max-width: 150px;"
                  >
                    {{ getLocalizedValue(getSnapshotValue(item, "type").name) || "-" }}
                  </span>
                  <span v-else>-</span>
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'relatedStandards') }">
                  <span v-b-tooltip.hover :title="getLocalizedValue(getSnapshotValue(item, 'relatedStandards'))" class="d-inline-block text-truncate" style="max-width: 150px;">
                    {{ getLocalizedValue(getSnapshotValue(item, "relatedStandards")) || "-" }}
                  </span>
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'risks') }">
                  <div class="text-wrap" style="min-width: 200px">
                    {{ formatArray(getSnapshotValue(item, "risks")) }}
                  </div>
                </td>

                <td :class="{ 'bg-soft-warning': isChanged(item, 'complianceItems') }">
                  <div class="text-wrap" style="min-width: 200px">
                    {{ formatArray(getSnapshotValue(item, "complianceItems")) }}
                  </div>
                </td>

                <!-- Approval Fields -->
                <td :class="{ 'bg-soft-warning': isChanged(item, 'approval') }">
                  <span v-if="getSnapshotValue(item, 'approval') && getSnapshotValue(item, 'approval').status">
                    <b-badge :variant="getApprovalStatusVariant(getSnapshotValue(item, 'approval').status)">
                      {{ $t(`audit-control.approval.status.${getSnapshotValue(item, "approval").status.toLowerCase()}`) }}
                    </b-badge>
                  </span>
                  <span v-else>-</span>
                </td>
                <td :class="{ 'bg-soft-warning': isChanged(item, 'approval') }">
                  <div v-if="getSnapshotValue(item, 'approval') && getSnapshotValue(item, 'approval').assignee">
                    {{ getSnapshotValue(item, "approval").assignee.name }}
                  </div>
                  <span v-else>-</span>
                </td>
                <td :class="{ 'bg-soft-warning': isChanged(item, 'approval') }">
                  <div v-if="getSnapshotValue(item, 'approval')">
                    <div v-if="getSnapshotValue(item, 'approval').reporterNote">
                      <small
                        ><b>{{ $t("audit-control.approval.reporterNote") }}:</b></small
                      ><br />
                      {{ getLocalizedValue(getSnapshotValue(item, "approval").reporterNote) }}
                    </div>
                    <div v-if="getSnapshotValue(item, 'approval').note" class="mt-1">
                      <small
                        ><b>{{ $t("audit-control.approval.note") }}:</b></small
                      ><br />
                      {{ getLocalizedValue(getSnapshotValue(item, "approval").note) }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="row mt-4">
          <div class="col">
            <b-pagination v-model="pagination.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" @change="pageChanged" limit="10" size="sm"></b-pagination>
          </div>
        </div>
      </div>
      <empty v-else>
        <h5 class="text-center">{{ $t("common.no-data") }}</h5>
      </empty>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    controlId: {
      type: String,
      required: true,
    },
  },
  computed: {
    pagination() {
      return this.$store.getters["organizationControlHistory/pagination"];
    },
    isLoading() {
      return this.$store.getters["organizationControlHistory/isLoading"];
    },
  },
  data() {
    return {
      request: {
        options: { page: 1, limit: 10, sort: { createdAt: "desc" } },
        query: {},
      },
    };
  },
  mounted() {
    this.fetchHistory();
  },
  methods: {
    fetchHistory() {
      this.$store.dispatch("organizationControlHistory/paginateWith", { pagination: this.request, controlId: this.controlId });
    },
    pageChanged(page) {
      this.request.options.page = page;
      this.fetchHistory();
    },
    getSnapshotValue(item, field) {
      if (!item.snapshot) return null;
      return item.snapshot[field];
    },
    isChanged(item, field) {
      return item.changes && item.changes[field] !== undefined;
    },
    formatArray(value) {
      if (!value || !Array.isArray(value) || value.length === 0) return "-";
      return value
        .map((v) => {
          if (typeof v === "object" && v !== null && v.name) {
            return this.getLocalizedValue(v.name);
          }
          return v;
        })
        .join(", ");
    },
    getLocalizedValue(val) {
      if (!val) return "";
      if (typeof val !== "object") return val;
      return val[this.$i18n.locale] || val.en || val.tr || "";
    },
    getFieldLabel(key) {
      const map = {
        name: this.$t("audit-control.control.field.name") || "Adı",
        status: this.$t("audit-control.control.field.status"),
        manager: this.$t("audit-control.control.field.manager"),
        criticality: this.$t("audit-control.control.field.criticality"),
        riskNote: this.$t("audit-control.control.field.riskNote"),
        category: this.$t("audit-control.control.category"),
        type: this.$t("audit-control.control.type"),
        relatedStandards: this.$t("audit-control.related-standards"),
        risks: this.$t("audit-control.control.field.risks"),
        complianceItems: this.$t("audit-control.control.field.complianceItems"),
        description: this.$t("risk.description"),
      };
      return map[key] || key;
    },
    formatValue(val, key) {
      if (val === null || val === undefined) return "-";
      
      if (typeof val === "string") {
        const isEnum = /^[A-Z0-9_]+$/.test(val);
        if (isEnum) {
          const enumKey = val.toLowerCase().replace(/_/g, '-');
          if (key === "status") return this.$te(`audit-control.status.${enumKey}`) ? this.$t(`audit-control.status.${enumKey}`) : val;
          if (key === "criticality") return this.$te(`audit-control.criticality.${enumKey}`) ? this.$t(`audit-control.criticality.${enumKey}`) : (this.$te(`audit-control.criticality.${val}`) ? this.$t(`audit-control.criticality.${val}`) : val);
        }
        return val;
      }

      if (Array.isArray(val)) {
        if (val.length === 0) return "[]";
        return val.map(v => {
          if (typeof v === 'object' && v !== null) {
            if (v.name) return this.getLocalizedValue(v.name);
            if (v.no) return v.no;
            if (v.tr || v.en || v[this.currentLanguage]) return v[this.currentLanguage] || v.tr || v.en;
            if (v._id) return v._id;
          }
          return v;
        }).join(", ");
      }

      if (typeof val === "object" && val !== null) {
        if (val.tr || val.en || val[this.currentLanguage]) {
          return val[this.currentLanguage] || val.tr || val.en;
        }
        if (val.name) return this.getLocalizedValue(val.name);
        if (val instanceof Date) return val.toLocaleDateString();
        return JSON.stringify(val);
      }
      return val;
    },
    getActionVariant(action) {
      switch (action) {
        case "CREATED":
          return "success";
        case "UPDATED":
          return "info";
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "danger";
        case "WAITING_FOR_APPROVAL":
          return "warning";
        default:
          return "primary";
      }
    },
    getStatusVariant(status) {
      switch (status) {
        case "ACTIVE":
          return "success";
        case "PASSIVE":
          return "secondary";
        case "WAITING_FOR_APPROVAL":
          return "warning";
        case "REJECTED":
          return "danger";
        case "COMPLETED":
          return "success";
        default:
          return "light";
      }
    },
    getApprovalStatusVariant(status) {
      switch (status) {
        case "PENDING":
          return "warning";
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "danger";
        default:
          return "secondary";
      }
    },
  },
};
</script>
